/**
 * Utility functions for consistent error handling across the application
 */

/**
 * Clears all authentication-related data from localStorage
 * Used when 401 errors occur to ensure clean logout state
 */
export const clearAuthStorage = () => {
  try {
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // Clear any other auth-related items that might exist
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('auth') || key.includes('token'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear auth storage:', error);
  }
};

/**
 * Handles API errors consistently - re-throws 401 errors for automatic logout/redirect
 * while allowing other errors to be handled normally
 * 
 * @param error - The caught error
 * @param context - Optional context for logging (e.g., 'dashboard loading')
 */
export const handleApiError = (error: any, context?: string) => {
  const logMessage = context ? `${context}:` : 'API error:';
  console.error(logMessage, error);
  
  // Re-throw 401 errors so API interceptor can handle logout/redirect
  if (error?.response?.status === 401) {
    throw error;
  }
  
  // For other errors, let the component handle them normally
};

/**
 * Global error handler that wraps async functions to ensure 401 errors are properly handled
 * Use this to wrap any async function that makes API calls
 * 
 * @param fn - The async function to wrap
 * @param context - Optional context for logging
 * @returns Wrapped function that handles 401 errors globally
 */
export const withGlobalErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: string
): T => {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error: any) {
      handleApiError(error, context);
      throw error; // Re-throw for component-specific handling
    }
  }) as T;
};

/**
 * Hook-style error handler for React components
 * Returns a function that can be used in try-catch blocks
 */
export const useGlobalErrorHandler = () => {
  return (error: any, context?: string) => {
    handleApiError(error, context);
  };
};

/**
 * Type guard to check if an error is a 401 Unauthorized error
 */
export const is401Error = (error: any): boolean => {
  return error?.response?.status === 401;
};

/**
 * Type guard to check if an error is a network/connection error
 */
export const isNetworkError = (error: any): boolean => {
  return !error?.response && error?.code === 'NETWORK_ERROR';
};

/**
 * Enhanced error handler that provides more context and handles different error types
 */
export const handleErrorWithFallback = (
  error: any, 
  context?: string, 
  fallbackMessage?: string
) => {
  const logMessage = context ? `${context}:` : 'API error:';
  console.error(logMessage, error);
  
  // Re-throw 401 errors so API interceptor can handle logout/redirect
  if (error?.response?.status === 401) {
    throw error;
  }
  
  // Return user-friendly error message for UI display
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return fallbackMessage || 'Une erreur inattendue s\'est produite';
};
